/**
 * v4 Settings - refined. Language + text size + about + log out.
 */

import { Link, useNavigate } from 'react-router-dom';
import { useEffect, useState } from 'react';
import { ArrowLeft, Languages, Type, Info, LogOut, CheckCircle2, Sun, Moon, Palette, Droplet, KeyRound, Loader2, Trash2, Bell, BellOff } from 'lucide-react';
import { toast } from 'sonner';
import { useV4, LANGS, FontSize, Lang, ThemeMode, Accent, ACCENTS } from './i18n';
import { cn } from '@/lib/utils';
import { useClient, logout, fetchSettings, saveSettings, setPin as apiSetPin, removePin as apiRemovePin, enablePushNotifications, disablePushNotifications, getPushStatus, setHasPinCached } from '@/services/api/client-auth';

export default function SettingsPageV4() {
  const { t, settings, setLang, setFontSize, setAccent, dir, themeMode, setThemeMode } = useV4();
  const navigate = useNavigate();
  const client = useClient();

  const [hasPin, setHasPin] = useState<boolean | null>(null);
  const [pinStep, setPinStep] = useState<'idle' | 'enter' | 'confirm'>('idle');
  const [pin1, setPin1] = useState('');
  const [pin2, setPin2] = useState('');
  const [pinBusy, setPinBusy] = useState(false);
  const [pushStatus, setPushStatus] = useState<'unsupported' | 'denied' | 'enabled' | 'disabled' | 'loading'>('loading');
  const [pushBusy, setPushBusy] = useState(false);

  useEffect(() => { getPushStatus().then(setPushStatus); }, []);

  async function togglePush() {
    setPushBusy(true);
    try {
      if (pushStatus === 'enabled') {
        await disablePushNotifications();
        setPushStatus('disabled');
        toast.success('Notifications turned off');
      } else {
        const r = await enablePushNotifications();
        if (r.ok) { setPushStatus('enabled'); toast.success('Notifications turned on'); }
        else { toast.error(r.reason); setPushStatus(await getPushStatus()); }
      }
    } finally { setPushBusy(false); }
  }

  useEffect(() => {
    fetchSettings().then(s => {
      setHasPin(s.hasPin);
      setHasPinCached(s.hasPin);
      if (s.lang && s.lang !== settings.lang) setLang(s.lang as Lang);
      if (s.themeMode && s.themeMode !== themeMode) setThemeMode(s.themeMode);
      if (s.fontSize && s.fontSize !== settings.fontSize) setFontSize(s.fontSize as FontSize);
      if (s.accent && s.accent !== settings.accent) setAccent(s.accent as Accent);
    }).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const persist = (patch: any) => { saveSettings(patch).catch(() => {}); };

  const onSetLang = (l: Lang) => { setLang(l); persist({ lang: l }); };
  const onSetTheme = (m: ThemeMode) => { setThemeMode(m); persist({ themeMode: m }); };
  const onSetFontSize = (f: FontSize) => { setFontSize(f); persist({ fontSize: f }); };
  const onSetAccent = (a: Accent) => { setAccent(a); persist({ accent: a }); };

  const onSignOut = () => {
    logout();
    navigate('/client/login', { replace: true });
  };

  const startSetPin = () => { setPin1(''); setPin2(''); setPinStep('enter'); };
  const cancelPin = () => { setPin1(''); setPin2(''); setPinStep('idle'); };

  const savePin = async (first: string, second: string) => {
    if (first !== second) {
      toast.error('PINs do not match');
      setPin1(''); setPin2(''); setPinStep('enter');
      return;
    }
    setPinBusy(true);
    try {
      await apiSetPin(first);
      setHasPin(true);
      setPinStep('idle');
      setPin1(''); setPin2('');
      toast.success('PIN saved');
    } catch (e: any) {
      toast.error(e?.response?.data?.error || 'Failed to set PIN');
      setPin1(''); setPin2(''); setPinStep('enter');
    } finally {
      setPinBusy(false);
    }
  };

  const onPinDigit = (d: string) => {
    if (pinBusy) return;
    if (pinStep === 'enter') {
      const next = (pin1 + d).slice(0, 4);
      setPin1(next);
      if (next.length === 4) {
        // Auto-advance to confirm step
        setTimeout(() => setPinStep('confirm'), 120);
      }
    } else {
      const next = (pin2 + d).slice(0, 4);
      setPin2(next);
      if (next.length === 4) {
        // Auto-submit
        setTimeout(() => savePin(pin1, next), 120);
      }
    }
  };

  const onPinBackspace = () => {
    if (pinBusy) return;
    if (pinStep === 'enter') setPin1(p => p.slice(0, -1));
    else setPin2(p => p.slice(0, -1));
  };

  const onRemovePin = async () => {
    setPinBusy(true);
    try {
      await apiRemovePin();
      setHasPin(false);
      toast.success('PIN removed');
    } catch (e: any) {
      toast.error(e?.response?.data?.error || 'Failed to remove PIN');
    } finally {
      setPinBusy(false);
    }
  };

  return (
    <div className="space-y-5 py-4 pb-12">
      <div className="flex items-center gap-2">
        <Link
          to="/client"
          className="flex h-8 w-8 items-center justify-center rounded-lg border border-border bg-card text-muted-foreground hover:border-muted-foreground/40"
          aria-label="Back"
        >
          <ArrowLeft className={`h-4 w-4 ${dir === 'rtl' ? 'rotate-180' : ''}`} />
        </Link>
        <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-muted-foreground">{t('settings')}</p>
      </div>

      {/* Chat Language picker hidden - the AI auto-detects the user's
          question language and replies in that same language. No setting
          needed. We keep the LANGS data + setLang callback wired so the
          picker can be re-enabled later if we add per-user UI translation. */}

      <Section icon={Palette} title={t('appearance')}>
        <div className="grid grid-cols-2 gap-2 p-3">
          {([
            { v: 'light', l: t('theme_light'), icon: Sun },
            { v: 'dark',  l: t('theme_dark'),  icon: Moon },
          ] as { v: ThemeMode; l: string; icon: any }[]).map(opt => {
            const isActive = themeMode === opt.v;
            const Icon = opt.icon;
            return (
              <button
                key={opt.v}
                onClick={() => onSetTheme(opt.v)}
                className={cn(
                  'flex items-center gap-2 rounded-xl border p-3 transition-all',
                  isActive
                    ? 'border-[var(--v4-accent)] bg-[var(--v4-accent-tint)] shadow-sm'
                    : 'border-border bg-card hover:border-muted-foreground/40'
                )}
              >
                <Icon className="h-4 w-4" />
                <span className="text-sm font-semibold flex-1 text-start">{opt.l}</span>
                {isActive && <CheckCircle2 className="h-4 w-4 text-[var(--v4-accent-text)]" />}
              </button>
            );
          })}
        </div>
      </Section>

      <Section icon={Droplet} title="Accent colour">
        <div className="grid grid-cols-4 gap-2 p-3">
          {(Object.entries(ACCENTS) as [Accent, typeof ACCENTS[Accent]][]).map(([key, def]) => {
            const isActive = settings.accent === key;
            return (
              <button
                key={key}
                onClick={() => onSetAccent(key)}
                title={def.name}
                aria-label={def.name}
                className={cn(
                  'group relative flex flex-col items-center gap-1 rounded-md border p-2 transition-all',
                  isActive ? 'border-foreground shadow-sm' : 'border-border hover:border-muted-foreground/40'
                )}
              >
                <span
                  className="relative flex h-9 w-9 items-center justify-center rounded-full"
                  style={{ backgroundColor: def.primary }}
                >
                  {isActive && <CheckCircle2 className="h-4 w-4 text-white" strokeWidth={3} />}
                </span>
                <span className="text-[10px] font-semibold text-muted-foreground">{def.name}</span>
              </button>
            );
          })}
        </div>
      </Section>

      <Section icon={Type} title={t('text_size')}>
        <div className="grid grid-cols-3 gap-2 p-3">
          {([
            { v: 'normal', l: t('text_normal'), size: 'text-sm' },
            { v: 'large',  l: t('text_large'),  size: 'text-base' },
            { v: 'xl',     l: t('text_xl'),     size: 'text-lg' },
          ] as { v: FontSize; l: string; size: string }[]).map(opt => {
            const isActive = settings.fontSize === opt.v;
            return (
              <button
                key={opt.v}
                onClick={() => onSetFontSize(opt.v)}
                className={cn(
                  'flex flex-col items-center gap-1 rounded-xl border px-3 py-3 transition-all',
                  isActive
                    ? 'border-[var(--v4-accent)] bg-[var(--v4-accent-tint)]'
                    : 'border-border bg-card hover:border-muted-foreground/40'
                )}
              >
                <span className={cn('font-bold text-foreground', opt.size)}>Aa</span>
                <span className="text-[10px] text-muted-foreground">{opt.l}</span>
              </button>
            );
          })}
        </div>
      </Section>

      <Section icon={Bell} title="Notifications">
        <div className="p-4 space-y-3">
          {pushStatus === 'unsupported' ? (
            <p className="text-sm text-muted-foreground">Your browser doesn't support push notifications.</p>
          ) : pushStatus === 'denied' ? (
            <p className="text-sm text-muted-foreground">
              Notifications are blocked in your browser. Open your browser settings for this site and allow notifications.
            </p>
          ) : (
            <>
              <p className="text-sm text-muted-foreground">
                {pushStatus === 'enabled'
                  ? 'You\'ll get a notification when your accountant sends a message.'
                  : 'Get notified when your accountant sends a message.'}
              </p>
              <button
                onClick={togglePush}
                disabled={pushBusy || pushStatus === 'loading'}
                className={cn(
                  'w-full rounded-lg py-2.5 text-sm font-semibold flex items-center justify-center gap-2 transition',
                  pushStatus === 'enabled'
                    ? 'border border-border bg-card text-foreground hover:bg-muted'
                    : 'text-white shadow-sm hover:shadow',
                )}
                style={pushStatus !== 'enabled' ? { background: 'var(--v4-accent)' } : undefined}
              >
                {pushBusy ? <Loader2 className="w-4 h-4 animate-spin" /> : pushStatus === 'enabled' ? <BellOff className="w-4 h-4" /> : <Bell className="w-4 h-4" />}
                {pushStatus === 'enabled' ? 'Turn off notifications' : 'Turn on notifications'}
              </button>
            </>
          )}
        </div>
      </Section>

      <Section icon={KeyRound} title="Quick sign-in PIN">
        <div className="p-4 space-y-3">
          {hasPin === null ? (
            <p className="text-sm text-muted-foreground flex items-center gap-2">
              <Loader2 className="w-3 h-3 animate-spin" /> Loading PIN status…
            </p>
          ) : pinStep === 'idle' ? (
            <>
              <p className="text-sm text-muted-foreground">
                {hasPin
                  ? 'A 4-digit PIN is set. The app will ask for it on every launch. Remove the PIN if you don\'t want to be prompted.'
                  : 'Set a 4-digit PIN to lock the app on every launch. Sign-in stays signed-in; the PIN just adds a second factor.'}
              </p>
              {hasPin ? (
                <button
                  onClick={onRemovePin}
                  disabled={pinBusy}
                  className="w-full flex items-center justify-center gap-2 rounded-lg border border-rose-200 dark:border-rose-900/60 bg-rose-50/60 dark:bg-rose-950/30 py-2.5 text-sm font-semibold text-rose-700 dark:text-rose-300 hover:border-rose-400 hover:bg-rose-50 dark:hover:bg-rose-950/50 disabled:opacity-50"
                >
                  {pinBusy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Trash2 className="w-4 h-4" />}
                  Remove PIN
                </button>
              ) : (
                <button
                  onClick={startSetPin}
                  className="w-full rounded-lg px-3 py-2.5 text-sm font-semibold text-white shadow-sm hover:shadow"
                  style={{ background: 'var(--v4-accent)' }}
                >
                  Set up PIN
                </button>
              )}
            </>
          ) : (
            <div className="flex flex-col items-center space-y-4">
              <p className="text-sm font-medium text-foreground">
                {pinStep === 'enter' ? 'Choose a 4-digit PIN' : 'Re-enter your PIN to confirm'}
              </p>

              {/* PIN dots */}
              <div className="flex items-center gap-3">
                {[0, 1, 2, 3].map(i => {
                  const value = pinStep === 'enter' ? pin1 : pin2;
                  const filled = value.length > i;
                  return (
                    <span
                      key={i}
                      className="h-3.5 w-3.5 rounded-full border-2 transition-all"
                      style={{
                        borderColor: filled ? 'var(--v4-accent)' : 'var(--border)',
                        background: filled ? 'var(--v4-accent)' : 'transparent',
                      }}
                    />
                  );
                })}
              </div>

              {pinBusy && <Loader2 className="w-4 h-4 animate-spin text-muted-foreground" />}

              {/* Numpad */}
              <div className="grid grid-cols-3 gap-2.5 w-full max-w-[240px]">
                {['1','2','3','4','5','6','7','8','9'].map(d => (
                  <button
                    key={d}
                    type="button"
                    onClick={() => onPinDigit(d)}
                    disabled={pinBusy}
                    className="h-12 rounded-xl border border-border bg-card text-xl font-semibold text-foreground active:scale-95 transition-transform disabled:opacity-50"
                  >
                    {d}
                  </button>
                ))}
                <button
                  type="button"
                  onClick={cancelPin}
                  disabled={pinBusy}
                  className="h-12 rounded-xl border border-border bg-card text-xs text-muted-foreground hover:text-foreground active:scale-95 transition-transform disabled:opacity-50"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={() => onPinDigit('0')}
                  disabled={pinBusy}
                  className="h-12 rounded-xl border border-border bg-card text-xl font-semibold text-foreground active:scale-95 transition-transform disabled:opacity-50"
                >
                  0
                </button>
                <button
                  type="button"
                  onClick={onPinBackspace}
                  disabled={pinBusy || (pinStep === 'enter' ? pin1.length === 0 : pin2.length === 0)}
                  className="h-12 rounded-xl border border-border bg-card text-muted-foreground hover:text-foreground active:scale-95 transition-transform disabled:opacity-30 flex items-center justify-center"
                  aria-label="Backspace"
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            </div>
          )}
        </div>
      </Section>

      <Section icon={Info} title={t('about')}>
        <div className="divide-y divide-border/60">
          <Row label="Signed in as" value={client?.displayName || '-'} />
          <Row label="Phone" value={client?.phone || '-'} />
          <Row label="Firm" value={client?.tenantName || t('firm_name')} />
          <Row label={t('app_name')} value="v4.0" />
        </div>
      </Section>

      <button
        onClick={onSignOut}
        className="flex w-full items-center justify-center gap-2 rounded-xl border border-rose-200 dark:border-rose-900/60 bg-rose-50/60 dark:bg-rose-950/30 p-3.5 text-sm font-semibold text-rose-700 dark:text-rose-300 transition-colors hover:border-rose-400 hover:bg-rose-50 dark:hover:bg-rose-950/50"
      >
        <LogOut className="h-4 w-4" />
        {t('log_out')}
      </button>
    </div>
  );
}

function Section({ icon: Icon, title, children }: { icon: any; title: string; children: React.ReactNode }) {
  return (
    <div>
      <h2 className="mb-2 flex items-center gap-1.5 px-1 text-[10px] font-bold uppercase tracking-[0.18em] text-muted-foreground">
        <Icon className="h-3 w-3" />
        {title}
      </h2>
      <div className="rounded-2xl border border-border bg-card">{children}</div>
    </div>
  );
}

function Row({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex items-center justify-between px-4 py-3 text-sm">
      <span className="text-muted-foreground">{label}</span>
      <span className="font-semibold text-foreground">{value}</span>
    </div>
  );
}
